/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.JOB.LIST
 *
 * Job descriptions maintenance. Each job points at exactly one approval
 * hierarchy (defined in CSM.HIERARCHY.LIST), and many jobs may share the
 * same hierarchy. When a new job is created the picker is pre-selected to
 * whichever hierarchy is flagged is_default='Y'.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveLocale } from '../../lib/use_active_locale';
import { buildTranslationsGroup } from '../../components/common/translations_group';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate, routedDelete } from '../../lib/routed_writes';
import { fetchJobDefaultRoles } from '../../lib/job_default_roles';
import type { AuthSession } from '../../types/auth';
import { FilteredModalSingleSelect } from '../../components/common/FilteredModalSingleSelect';
import { SortableHeader } from '../../components/common/SortableHeader';
import { YesNoBadge } from '../../components/common/YesNoBadge';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { compareStrings, useSort } from '../../lib/use_sort';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import { usePersonalization } from '../../lib/use_personalization';
import type { GridColumnDef } from '../../components/personalization';
import { Modal } from '../../components/common/Modal';
import { FieldRenderer, toUldCoreLayout } from '../../components/common/FieldRenderer';
import type { FieldDef } from '../../components/common/FieldRenderer';
import { useUldLayout } from '../../hooks/use_uld_layout';
import { UdcFacets } from '../../components/common/UdcFacets';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useUdcGrid } from '../../hooks/use_udc_grid';
import { useUdcFormFields } from '../../hooks/use_udc_form_fields';
import { registerUdcHost } from '../../lib/udc_hosts';
import { TranslationsEditor } from '../../components/common/TranslationsEditor';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { hasAccess } from '../../types/auth';
import { registerChildEditor, type ChildEditorProps } from '../../lib/child_editors';

const PAGE_CODE = 'CSM.JOB.LIST';

registerUdcHost(PAGE_CODE, 'csm_job_description', 'Job Descriptions');

type JobSortKey = 'internal_code' | 'display_text' | 'hierarchy_label' | 'is_active';

const JOB_FIELD_DEFS: FieldDef[] = [
  { key: 'internal_code', type: 'text', label: 'Internal Code', required: true, col_span: 1 },
  { key: 'is_active', type: 'boolean', label: 'Active', col_span: 1 },
];
const JOB_CORE_LAYOUT = toUldCoreLayout(JOB_FIELD_DEFS);

interface JobRow {
  job_description_id: number;
  internal_code: string;
  is_active: 'Y' | 'N';
  apr_status: string;
  display_text: string;
  approval_hierarchy_id: number | null;
  hierarchy_label: string;
  /** Every locale's text, kept so the related panel can list them (§17.B14). */
  tl: { locale_code: string; display_text: string }[];
}

interface HierarchyOpt {
  approval_hierarchy_id: number;
  internal_code: string;
  display_text: string;
  is_default: 'Y' | 'N';
  step_count: number;
}

interface TLRow {
  locale_code: string;
  display_text: string;
  description: string;
}

// Related-children (expand/contract detail panel) row shapes - keyed by job.
interface RoleChild { role_id: number; name: string; code: string; }
interface UserChild { user_id: number; label: string; email: string; }
interface ApproverChild { user_id: number; label: string; sequence_order: number; }

const FILTER_KEY = 'csm.jobs.filter';

const GRID_COLS: GridColumnDef[] = [
  { key: 'internal_code',   header: 'Code',      defaultWidth: 160 },
  { key: 'display_text',    header: 'Name',      defaultWidth: 200 },
  { key: 'hierarchy_label', header: 'Hierarchy', defaultWidth: 200 },
  { key: 'apr_status',      header: 'Approval',  defaultWidth: 150 },
  { key: 'is_active',       header: 'Active',    defaultWidth: 80  },
];

// Approval-state pill for the authorization demo: a new job title sits at
// PENDING_APPROVAL until the b → c → d sign-off chain completes (APPROVED), or is
// REJECTED. Rows pre-dating the demo default to APPROVED. (§17.B11)
const APR_STATUS_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  APPROVED:         { labelKey: 'job_description.list.apr_status.APPROVED',         label: 'Approved',         variant: 'success' },
  PENDING_APPROVAL: { labelKey: 'job_description.list.apr_status.PENDING_APPROVAL', label: 'Pending approval', variant: 'warning' },
  REJECTED:         { labelKey: 'job_description.list.apr_status.REJECTED',         label: 'Rejected',         variant: 'danger'  },
};

function AprStatusBadge({ status }: { status: string }) {
  const { t } = useTranslation();
  const meta = APR_STATUS_META[status];
  return <StatusBadge variant={meta?.variant ?? 'neutral'} label={meta ? t(meta.labelKey, meta.label) : (status || '—')} />;
}

export interface JobDescriptionPageProps {
  session: AuthSession;
}

export function JobDescriptionPage({ session }: JobDescriptionPageProps) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <JobDescriptionPageInner session={session} />
    </PersonalizationProvider>
  );
}

function JobDescriptionPageInner({ session }: JobDescriptionPageProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canAdd = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  // §8: resolve _tl data against the ACTIVE locale (follows a runtime switch), not
  // the stored preferred_locale (stale in memory until re-login).
  const activeLocale = useActiveLocale();
  const { isPersonalizing } = usePersonalization();
  const detail = useExpandedRows<number>();
  const [rows, setRows]     = useState<JobRow[]>([]);
  const [hierarchies, setHierarchies] = useState<HierarchyOpt[]>([]);
  // Related-children detail panel data, keyed by job_description_id.
  const [rolesByJob, setRolesByJob]         = useState<Map<number, RoleChild[]>>(new Map());
  const [usersByJob, setUsersByJob]         = useState<Map<number, UserChild[]>>(new Map());
  const [approversByJob, setApproversByJob] = useState<Map<number, ApproverChild[]>>(new Map());
  const [filter, setFilter] = useState(() => sessionStorage.getItem(FILTER_KEY) ?? '');
  // §17.B5: per-column multi-value facets for the jobs list.
  const [facets, setFacets] = useState<{ hierarchy: string[]; is_active: string[] }>({ hierarchy: [], is_active: ['Y'] });
  const [editing, setEditing]   = useState<JobRow | null>(null);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sort = useSort<JobSortKey>('csm.jobs.sort', { key: 'internal_code', dir: 'asc' });

  useEffect(() => { sessionStorage.setItem(FILTER_KEY, filter); }, [filter]);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const locale = activeLocale;

      // Hierarchies for the picker.
      const { data: hData, error: hErr } = await apiDb
        .from('csm_approval_hierarchy')
        .select(`
          approval_hierarchy_id, internal_code, is_default, is_active,
          csm_approval_hierarchy_tl ( locale_code, display_text ),
          csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )
        `)
        .eq('is_active', 'Y')
        .order('internal_code');
      if (hErr) throw hErr;
      type RawHierOpt = {
        approval_hierarchy_id: number;
        internal_code: string;
        is_default: 'Y' | 'N';
        is_active: 'Y' | 'N';
        csm_approval_hierarchy_tl?: TlRow[];
        csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: 'Y' | 'N' }[];
      };
      const hOpts: HierarchyOpt[] = (hData ?? []).map((h: RawHierOpt) => {
        const tls = h.csm_approval_hierarchy_tl ?? [];
        const exact = tls.find((t: TlRow) => t.locale_code === locale);
        const fb    = tls.find((t: TlRow) => t.locale_code === 'EN_US') ?? tls[0];
        const stepCount = (h.csm_approval_hierarchy_step ?? []).filter((s) => s.is_active === 'Y').length;
        return {
          approval_hierarchy_id: h.approval_hierarchy_id,
          internal_code: h.internal_code,
          display_text: exact?.display_text ?? fb?.display_text ?? h.internal_code,
          is_default: h.is_default,
          step_count: stepCount,
        };
      });
      setHierarchies(hOpts);

      // Jobs.
      const { data, error: e } = await apiDb
        .from('csm_job_description')
        .select(`
          job_description_id, internal_code, is_active, apr_status, approval_hierarchy_id,
          csm_job_description_tl ( locale_code, display_text ),
          csm_approval_hierarchy:approval_hierarchy_id (
            internal_code, csm_approval_hierarchy_tl ( locale_code, display_text )
          )
        `)
        .order('internal_code');
      if (e) throw e;
      type RawJob = {
        job_description_id: number;
        internal_code: string;
        is_active: 'Y' | 'N';
        apr_status: string;
        approval_hierarchy_id: number | null;
        csm_job_description_tl?: TlRow[];
        csm_approval_hierarchy?: { internal_code?: string; csm_approval_hierarchy_tl?: TlRow[] } | null;
      };
      const list: JobRow[] = (data ?? []).map((j: RawJob) => {
        const tls = j.csm_job_description_tl ?? [];
        const exact = tls.find((t: TlRow) => t.locale_code === locale);
        const fb    = tls.find((t: TlRow) => t.locale_code === 'EN_US') ?? tls[0];

        const hTls = j.csm_approval_hierarchy?.csm_approval_hierarchy_tl ?? [];
        const hExact = hTls.find((t: TlRow) => t.locale_code === locale);
        const hFb    = hTls.find((t: TlRow) => t.locale_code === 'EN_US') ?? hTls[0];

        return {
          job_description_id: j.job_description_id,
          internal_code: j.internal_code,
          is_active: j.is_active,
          apr_status: j.apr_status,
          display_text: exact?.display_text ?? fb?.display_text ?? j.internal_code,
          tl: tls,
          approval_hierarchy_id: j.approval_hierarchy_id,
          hierarchy_label: j.approval_hierarchy_id == null
            ? ''
            : (hExact?.display_text ?? hFb?.display_text ?? j.csm_approval_hierarchy?.internal_code ?? ''),
        };
      });
      setRows(list);

      // ── Related children for the expand/contract detail panel ───────────────
      // roles linked to each job (csm_job_description_role -> csm_role_master),
      // users holding each job (csm_user_master), and the job's approver chain
      // (csm_job_description_approver -> csm_user_master, in sequence order).
      const [jdrRes, rolesRes, usersRes, apprRes] = await Promise.all([
        apiDb.from('csm_job_description_role').select('job_description_id, role_id'),
        apiDb.from('csm_role_master').select('role_id, internal_code, csm_role_master_tl ( locale_code, display_text )'),
        apiDb.from('csm_user_master').select('user_id, job_description_id, display_name, email'),
        apiDb.from('csm_job_description_approver').select('job_description_id, user_id, sequence_order').order('sequence_order'),
      ]);

      type Tl = { locale_code: string; display_text: string };
      const pickTl = (tls: Tl[] | undefined, code: string) => {
        const tlList = tls ?? [];
        return tlList.find(x => x.locale_code === locale)?.display_text
          ?? tlList.find(x => x.locale_code === 'EN_US')?.display_text
          ?? tlList[0]?.display_text ?? code;
      };

      type RawRole = { role_id: number; internal_code: string; csm_role_master_tl?: Tl[] };
      const roleInfo = new Map<number, { code: string; name: string }>();
      for (const r of (rolesRes.data ?? []) as RawRole[]) {
        roleInfo.set(r.role_id, { code: r.internal_code, name: pickTl(r.csm_role_master_tl, r.internal_code) });
      }

      type RawUser = { user_id: number; job_description_id: number | null; display_name: string | null; email: string | null };
      const userInfo = new Map<number, { name: string; email: string }>();
      const uMap = new Map<number, UserChild[]>();
      for (const u of (usersRes.data ?? []) as RawUser[]) {
        const name = u.display_name ?? u.email ?? '';
        userInfo.set(u.user_id, { name, email: u.email ?? '' });
        if (u.job_description_id != null) {
          const arr = uMap.get(u.job_description_id) ?? [];
          arr.push({ user_id: u.user_id, label: name, email: u.email ?? '' });
          uMap.set(u.job_description_id, arr);
        }
      }

      type RawJdr = { job_description_id: number; role_id: number };
      const rMap = new Map<number, RoleChild[]>();
      for (const jr of (jdrRes.data ?? []) as RawJdr[]) {
        const info = roleInfo.get(jr.role_id);
        if (!info) continue;
        const arr = rMap.get(jr.job_description_id) ?? [];
        arr.push({ role_id: jr.role_id, name: info.name, code: info.code });
        rMap.set(jr.job_description_id, arr);
      }

      type RawApprover = { job_description_id: number; user_id: number; sequence_order: number };
      const aMap = new Map<number, ApproverChild[]>();
      for (const a of (apprRes.data ?? []) as RawApprover[]) {
        const info = userInfo.get(a.user_id);
        const arr = aMap.get(a.job_description_id) ?? [];
        arr.push({ user_id: a.user_id, label: info?.name ?? '', sequence_order: a.sequence_order });
        aMap.set(a.job_description_id, arr);
      }

      for (const arr of rMap.values()) arr.sort((x, y) => x.name.localeCompare(y.name));
      for (const arr of uMap.values()) arr.sort((x, y) => x.label.localeCompare(y.label));
      // approvers already arrive ordered by sequence_order.
      setRolesByJob(rMap);
      setUsersByJob(uMap);
      setApproversByJob(aMap);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // reload is re-created each render; the effect runs on mount and re-resolves _tl
  // display text on a runtime locale switch (§8). reload is not a dependency.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void reload();   }, [activeLocale]);

  const handleDelete = async (j: JobRow) => {
    setBusy(true); setError(null);
    try {
      const { count: userCount, error: uErr } = await apiDb
        .from('csm_user_master')
        .select('user_id', { head: true, count: 'exact' })
        .eq('job_description_id', j.job_description_id);
      if (uErr) throw uErr;
      if ((userCount ?? 0) > 0) {
        throw new Error(
          t('job_description.list.errors.users_reference', { count: userCount ?? 0 }),
        );
      }
      const { count: reqCount, error: rErr } = await apiDb
        .from('csm_user_provision_request')
        .select('user_provision_request_id', { head: true, count: 'exact' })
        .eq('target_job_description_id', j.job_description_id);
      if (rErr) throw rErr;
      if ((reqCount ?? 0) > 0) {
        throw new Error(
          t('job_description.list.errors.requests_reference', { count: reqCount ?? 0 }),
        );
      }
      if (!confirm(t('job_description.list.errors.confirm_delete', { code: j.internal_code }))) {
        setBusy(false); return;
      }
      const { error: dErr } = await routedDelete(session, 'csm_job_description', 'job_description_id', j.job_description_id);
      if (dErr) throw dErr;
      await reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // User-defined columns / facets an admin placed on this page (runtime, no code).
  const udcGrid = useUdcGrid('csm_job_description', PAGE_CODE, { pkColumn: 'job_description_id', rows: rows as unknown as Record<string, unknown>[] });

  // Distinct facet options derived from the loaded rows (§17.B5).
  const facetOptions = useMemo(() => {
    const labelOf = (r: JobRow) => r.hierarchy_label || t('common.none');
    const uniq = [...new Set(rows.map(labelOf).filter(Boolean))].sort().map(v => ({ value: v, label: v }));
    return {
      hierarchy: uniq,
      is_active: [{ value: 'Y', label: t('common.boolean.active') }, { value: 'N', label: t('common.boolean.inactive') }],
    };
  }, [rows, t]);

  const facetsActive = facets.hierarchy.length + facets.is_active.length > 0;
  const anyFilterActive = facetsActive || filter.trim().length > 0;
  const clearAll = () => { setFilter(''); setFacets({ hierarchy: [], is_active: [] }); };

  const filtered = useMemo(() => {
    const hierLabelOf = (r: JobRow) => r.hierarchy_label || t('common.none');
    const matchesFacets = (r: JobRow) =>
      (facets.hierarchy.length === 0 || facets.hierarchy.includes(hierLabelOf(r))) &&
      (facets.is_active.length === 0 || facets.is_active.includes(r.is_active));
    const base = (!filter.trim()
      ? rows
      : rows.filter(r => {
          const q = filter.toLowerCase();
          return r.internal_code.toLowerCase().includes(q) ||
            r.display_text.toLowerCase().includes(q) ||
            r.hierarchy_label.toLowerCase().includes(q);
        })).filter(matchesFacets).filter(r => udcGrid.matchRow(r as unknown as Record<string, unknown>));
    return sort.sorted(base, (a, b, key) => {
      if (udcGrid.isUdcKey(key)) return udcGrid.compare(a as unknown as Record<string, unknown>, b as unknown as Record<string, unknown>, key);
      return compareStrings(a[key], b[key]);
    });
  }, [rows, filter, facets, sort, udcGrid, t]);

  const hasRelated = (id: number) =>
    (rolesByJob.get(id)?.length ?? 0) > 0 ||
    (usersByJob.get(id)?.length ?? 0) > 0 ||
    (approversByJob.get(id)?.length ?? 0) > 0 ||
    (rows.find(r => r.job_description_id === id)?.tl.length ?? 0) > 0;

  const buildRelatedGroups = (id: number): RelatedChildGroup[] => [
    {
      key: 'roles',
      table: 'csm_role_master', pkColumn: 'role_id',
      title: t('jobs.list.related.roles', 'Roles'),
      items: (rolesByJob.get(id) ?? []).map(r => ({ id: r.role_id, label: r.name, title: r.code })),
    },
    {
      key: 'users',
      table: 'csm_user_master', pkColumn: 'user_id',
      title: t('jobs.list.related.users', 'Users'),
      items: (usersByJob.get(id) ?? []).map(u => ({ id: u.user_id, label: u.label, title: u.email })),
    },
    {
      key: 'approvers',
      table: 'csm_user_master', pkColumn: 'user_id',
      title: t('jobs.list.related.approvers', 'Approvers'),
      items: (approversByJob.get(id) ?? []).map(a => ({
        id: `${a.user_id}:${a.sequence_order}`,
        label: a.label,
        badges: [String(a.sequence_order)],
      })),
    },
    buildTranslationsGroup(rows.find(r => r.job_description_id === id)?.tl, t, activeLocale),
  ];

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('job_description.list.title', 'Job Descriptions')}
          description={t('job_description.list.description', 'Job titles, their approval hierarchy and the roles pre-selected on provisioning requests.')}
          actions={
            <>
              <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5">
                <RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh')}
              </button>
              {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5">
                <Plus className="h-3.5 w-3.5" /> {t('job_description.list.new_job')}
              </button>)}
            </>
          }
        />
        {error && (
          <ErrorBanner
            message={t('job_description.list.error_banner', "Couldn't complete the last job-descriptions operation.")}
            detail={error}
            retry_label={t('common.retry', 'Retry')}
            on_retry={() => void reload()}
            details_label={t('common.details', 'Details')}
          />
        )}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input
                type="text" value={filter} onChange={e => setFilter(e.target.value)}
                placeholder={t('job_description.list.search_placeholder')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none"
              />
            </div>
            <FacetFilter label={t('job_description.list.columns.hierarchy', 'Hierarchy')} options={facetOptions.hierarchy} selected={facets.hierarchy} onChange={v => setFacets(f => ({ ...f, hierarchy: v }))} />
            <FacetFilter label={t('job_description.list.columns.active', 'Active')}        options={facetOptions.is_active} selected={facets.is_active} onChange={v => setFacets(f => ({ ...f, is_active: v }))} />
            <UdcFacets grid={udcGrid} />
            <ClearFiltersButton active={anyFilterActive} onClear={clearAll} label={t('common.clear_filters', 'Clear filters')} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          {isPersonalizing && (
            <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
              {t('job_description.list.personalization_banner', 'Personalization mode — configure columns, then click the icon to save and exit.')}
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-auto">
            {busy && rows.length === 0 ? <SkeletonRows rows={8} /> : (
        <PersonalizedGrid componentKey="grid" columns={[...GRID_COLS, ...udcGrid.columns]}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="w-7 border-b border-csm-border px-1 py-1.5"></th>
                  {visibleCols.map(col => (
                    <SortableHeader<JobSortKey> key={col.key}
                      sortKey={col.key as JobSortKey} label={col.header}
                      currentKey={sort.sortKey} currentDir={sort.sortDir} onSort={sort.setSort}
                      style={widths[col.key] ? { width: widths[col.key], minWidth: widths[col.key] } : undefined} />
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('job_description.list.columns.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(j => (
                  <Fragment key={j.job_description_id}>
                  <tr className="hover:bg-csm-surface-alt">
                    <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle" onClick={(e) => e.stopPropagation()}>
                      <RelatedExpandButton
                        open={detail.isOpen(j.job_description_id)}
                        hasChildren={hasRelated(j.job_description_id)}
                        onToggle={() => detail.toggle(j.job_description_id)}
                        expandLabel={t('jobs.list.related.expand_aria', 'Show related records')}
                        collapseLabel={t('jobs.list.related.collapse_aria', 'Hide related')}
                        tooltip={t('jobs.list.related.tooltip', 'Show related records')}
                      />
                    </td>
                    {visibleCols.map(col => (
                      <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5"
                          style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                        {col.key === 'internal_code' && <span className="font-mono text-csm-sm">{j.internal_code}</span>}
                        {col.key === 'display_text' && j.display_text}
                        {col.key === 'hierarchy_label' && (
                          j.hierarchy_label
                            ? j.hierarchy_label
                            : <span className="italic text-csm-watermark">{t('common.none')}</span>
                        )}
                        {col.key === 'apr_status' && <AprStatusBadge status={j.apr_status} />}
                        {col.key === 'is_active' && <YesNoBadge value={j.is_active} yesLabel={t('common.boolean.active')} noLabel={t('common.boolean.inactive')} />}
                        {udcGrid.isUdcKey(col.key) && udcGrid.renderCell(j as unknown as Record<string, unknown>, col.key)}
                      </td>
                    ))}
                    <td className="border-b border-csm-border-soft px-3 py-1.5 text-right whitespace-nowrap">
                      {canChange && (<button onClick={(e) => { e.stopPropagation(); setEditing(j); }} className="mr-1 inline-block min-w-[3.25rem] rounded border border-csm-border bg-white px-2 py-0.5 text-csm-xs hover:bg-csm-border-soft">{t('job_description.list.actions.edit')}</button>)}
                      <button
                        onClick={(e) => { e.stopPropagation(); void handleDelete(j); }}
                        title={t('job_description.list.actions.delete_tooltip')}
                        className="inline-block min-w-[3.75rem] rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft"
                      >{t('job_description.list.actions.delete')}</button>
                    </td>
                  </tr>
                  {detail.isOpen(j.job_description_id) && (
                    <tr>
                      <td
                        colSpan={visibleCols.length + 2}
                        className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <RelatedChildrenPanel groups={buildRelatedGroups(j.job_description_id)} session={session} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="px-3 py-8 text-center">
                    <EmptyState
                      variant={anyFilterActive ? 'no-match' : 'no-data'}
                      title={t('job_description.list.empty')}
                      action={anyFilterActive ? undefined : canAdd && (<button onClick={() => setCreating(true)} className="mt-2 h-7 rounded border border-primary bg-primary px-2 text-csm-sm text-white hover:bg-primary-hover">{t('job_description.list.new_job')}</button>)}
                    />
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
            </PersonalizedGrid>
            )}
          </div>
        </ContentCard>
      </div>

      {(editing || creating) && (
        <JobEditModal
          session={session}
          row={editing}
          hierarchies={hierarchies}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); void reload(); }}
        />
      )}
    </div>
  );
}

// =============================================================================
// EDIT DRAWER
// =============================================================================

interface JobEditModalProps {
  session: AuthSession;
  row: JobRow | null;
  hierarchies: HierarchyOpt[];
  onClose: () => void;
  onSaved: () => void;
}

function JobEditModal({ session, row, hierarchies, onClose, onSaved }: JobEditModalProps) {
  const { t } = useTranslation();
  const { isPersonalizing } = usePersonalization();
  const CK = 'drawer';
  const isCreate = row === null;
  // Custom fields placed on this entity (host = the list page) become first-class
  // FieldRenderer items: they interleave with native fields and inherit the same
  // per-tier personalization (hide / read-only / required / reorder).
  const udcForm = useUdcFormFields('csm_job_description', PAGE_CODE, { pkColumn: 'job_description_id', pk: row?.job_description_id });
  const jobCoreLayout = useMemo(() => [...JOB_CORE_LAYOUT, ...udcForm.coreLayout], [udcForm.coreLayout]);
  const jobFieldDefs = useMemo(() => [...JOB_FIELD_DEFS, ...udcForm.fieldDefs], [udcForm.fieldDefs]);
  const { layout } = useUldLayout('CSM.JOBDESC.MODAL', jobCoreLayout);
  const defaultHier = hierarchies.find(h => h.is_default === 'Y');

  const [internalCode, setInternalCode] = useState(row?.internal_code ?? '');
  const [isActive, setIsActive]         = useState(row ? row.is_active === 'Y' : true);
  const [hierarchyId, setHierarchyId]   = useState<number | null>(
    isCreate ? (defaultHier?.approval_hierarchy_id ?? null) : (row?.approval_hierarchy_id ?? null),
  );
  const [translations, setTranslations] = useState<TLRow[]>([]);
  const activeLocale = useActiveLocale();
  const [allRoles, setAllRoles] = useState<{ role_id: number; internal_code: string; display_text: string }[]>([]);
  const [selectedRoleIds, setSelectedRoleIds] = useState<Set<number>>(new Set());
  const [initialRoleIds, setInitialRoleIds] = useState<number[]>([]);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isCreate || !row) {
      setTranslations([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error: e } = await apiDb
        .from('csm_job_description_tl')
        .select('locale_code, display_text, description')
        .eq('job_description_id', row.job_description_id);
      if (cancelled) return;
      if (e) { setError(e.message); return; }
      const list: TLRow[] = (data ?? []).map((t: TlRow) => ({
        locale_code: t.locale_code, display_text: t.display_text ?? '', description: t.description ?? '',
      }));
      setTranslations(list);
    })();
    return () => { cancelled = true; };
  }, [isCreate, row?.job_description_id]); // eslint-disable-line

  // Active roles for the multi-select (localized labels).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const { data } = await apiDb
        .from('csm_role_master')
        .select('role_id, internal_code, is_active, csm_role_master_tl ( locale_code, display_text )')
        .eq('is_active', 'Y')
        .order('internal_code');
      if (cancelled) return;
      type R = { role_id: number; internal_code: string; csm_role_master_tl?: TlRow[] };
      const list = (data ?? []).map((r: R) => {
        const tls = r.csm_role_master_tl ?? [];
        const exact = tls.find(x => x.locale_code === activeLocale);
        const fb    = tls.find(x => x.locale_code === 'EN_US') ?? tls[0];
        return { role_id: r.role_id, internal_code: r.internal_code, display_text: exact?.display_text ?? fb?.display_text ?? r.internal_code };
      });
      setAllRoles(list);
    })();
    return () => { cancelled = true; };
  }, [activeLocale]);

  // Existing default roles for this job (edit mode only).
  useEffect(() => {
    if (isCreate || !row) { setSelectedRoleIds(new Set()); setInitialRoleIds([]); return; }
    let cancelled = false;
    void (async () => {
      const ids = await fetchJobDefaultRoles(row.job_description_id);
      if (cancelled) return;
      setSelectedRoleIds(new Set(ids));
      setInitialRoleIds(ids);
    })();
    return () => { cancelled = true; };
  }, [isCreate, row?.job_description_id]); // eslint-disable-line

  const selectedHier = hierarchies.find(h => h.approval_hierarchy_id === hierarchyId);

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (hierarchyId == null) throw new Error(t('job_description.drawer.errors.pick_hierarchy', 'Pick an approval hierarchy.'));
      let jobId = row?.job_description_id ?? null;

      if (isCreate) {
        const { row_id, error: e } = await routedInsert(session, 'csm_job_description', {
          internal_code: internalCode,
          is_active: isActive ? 'Y' : 'N',
          approval_hierarchy_id: hierarchyId,
        });
        if (e) throw e;
        jobId = row_id;
      } else {
        const { error: e } = await routedUpdate(session, 'csm_job_description', 'job_description_id', row?.job_description_id, {
          is_active: isActive ? 'Y' : 'N',
          approval_hierarchy_id: hierarchyId,
        });
        if (e) throw e;
      }

      for (const t of translations) {
        if (!t.locale_code || !t.display_text) continue;
        const { error: e } = await apiDb
          .from('csm_job_description_tl')
          .upsert({
            job_description_id: jobId,
            locale_code: t.locale_code,
            display_text: t.display_text,
            description: t.description || null,
          }, { onConflict: 'job_description_id,locale_code' });
        if (e) throw e;
      }

      // Diff default-role links and write through the Common Routine.
      // Assumes links are HARD-deleted (the table is not GTC-registered), so a
      // removed pair leaves no is_active='N' row. If soft-delete is ever added,
      // switch this insert to an upsert/reactivate to avoid the UNIQUE(job,role) clash.
      // jobId is set from row_id on create and from the edited row otherwise, so
      // it is always present here — assert it as a real guard rather than a `!`,
      // so an unexpected null surfaces as an error instead of a silent bad write.
      if (jobId == null) throw new Error(t('job_description.drawer.errors.no_id', 'Could not resolve the job description.'));
      const target = jobId;
      const before = new Set(initialRoleIds);
      const after  = selectedRoleIds;
      const toAdd    = [...after].filter(id => !before.has(id));
      const toRemove = [...before].filter(id => !after.has(id));

      for (const roleId of toAdd) {
        const { error: e } = await routedInsert(session, 'csm_job_description_role', {
          job_description_id: target,
          role_id: roleId,
          is_active: 'Y',
        });
        if (e) throw e;
      }
      for (const roleId of toRemove) {
        const { data: link, error: eSel } = await apiDb
          .from('csm_job_description_role')
          .select('job_description_role_id')
          .eq('job_description_id', target)
          .eq('role_id', roleId)
          .maybeSingle();
        if (eSel) throw eSel;
        const linkId = (link as { job_description_role_id: number } | null)?.job_description_role_id;
        if (linkId != null) {
          const { error: e } = await routedDelete(session, 'csm_job_description_role', 'job_description_role_id', linkId);
          if (e) throw e;
        }
      }

      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isCreate ? t('job_description.drawer.create_title', 'New Job Description') : t('job_description.drawer.edit_title', 'Edit Job Description - {{code}}', { code: row?.internal_code ?? '' })}
      onClose={onClose}
      headerAction={<>{session.effective_user.page_access.length > 0 && session.effective_user.page_access.every(p => p.can_view && p.can_change) && null}<PersonalizationToggle /></>}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy || (isCreate && !internalCode) || hierarchyId == null}
                  className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">
            {busy ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
          </button>
        </>
      }
    >
      {isPersonalizing && (
        <div className="-mx-4 -mt-4 mb-4 border-b border-primary/20 bg-primary/5 px-3 py-1 text-csm-xs text-primary">
          {t('job_description.drawer.personalization_banner', 'Toggle fields — hide or show. Click icon to save.')}
        </div>
      )}
      <div className="mb-3">
        <FieldRenderer
          fields={jobFieldDefs}
          layout={layout}
          columns={2}
          pageCode="CSM.JOBDESC.MODAL"
          session={session}
          render={(f) => {
            if (f.key === 'internal_code') return (
              <PersonalizedField componentKey={CK} fieldKey="internal_code" label={t('job_description.drawer.field.internal_code', 'Internal Code')}>
                <div>
                  <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">{t('job_description.drawer.field.internal_code', 'Internal Code')}</label>
                  <input
                    type="text" value={internalCode} disabled={!isCreate}
                    onChange={e => setInternalCode(e.target.value.toUpperCase())}
                    placeholder={t('job_description.drawer.field.internal_code_placeholder', 'DOMAIN.ROLE.NAME')}
                    className="h-8 w-full rounded border border-csm-border px-2 font-mono text-csm-sm focus:border-csm-watermark focus:outline-none disabled:bg-csm-surface-alt disabled:text-csm-muted"
                  />
                </div>
              </PersonalizedField>
            );
            if (f.key === 'is_active') return (
              <PersonalizedField componentKey={CK} fieldKey="is_active" label={t('job_description.drawer.field.active', 'Active')}>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-csm-sm text-csm-ink-soft">
                    <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="h-4 w-4 accent-gray-700" />
                    {t('job_description.drawer.field.active_checkbox', 'Active (Yes / No)')}
                  </label>
                </div>
              </PersonalizedField>
            );
            if (udcForm.isUdcKey(f.key)) return (
              <PersonalizedField componentKey={CK} fieldKey={f.key} label={f.label}
                readonlyChildren={udcForm.renderControl(f.key, { disabled: true })}>
                {udcForm.renderControl(f.key)}
              </PersonalizedField>
            );
            return null;
          }}
        />
      </div>

      <PersonalizedField componentKey={CK} fieldKey="approval_hierarchy" label={t('job_description.drawer.hierarchy.label', 'Approval Hierarchy')}>
        <div className="mb-3">
          <FilteredModalSingleSelect
            label={t('job_description.drawer.hierarchy.label', 'Approval Hierarchy')}
            options={hierarchies.map(h => ({
              internal_code: h.internal_code,
              display_text: h.display_text + (h.is_default === 'Y' ? t('job_description.drawer.hierarchy.default_suffix', '  (default)') : ''),
              description: `${h.internal_code} - ${h.step_count === 1
                ? t('job_description.drawer.hierarchy.approver_one', '{{count}} approver', { count: h.step_count })
                : t('job_description.drawer.hierarchy.approver_other', '{{count}} approvers', { count: h.step_count })}`,
            }))}
            selectedCode={
              hierarchyId == null
                ? null
                : hierarchies.find(h => h.approval_hierarchy_id === hierarchyId)?.internal_code ?? null
            }
            onChange={(code) => {
              if (code === null) setHierarchyId(null);
              else {
                const h = hierarchies.find(hh => hh.internal_code === code);
                setHierarchyId(h?.approval_hierarchy_id ?? null);
              }
            }}
            placeholder={t('job_description.drawer.hierarchy.placeholder', 'Select an approval hierarchy...')}
            allowClear={false}
          />
          {selectedHier && (
            <div className={
              'mt-1 text-csm-2xs ' + (selectedHier.step_count === 0 ? 'text-csm-danger' : 'text-csm-muted')
            }>
              {selectedHier.step_count === 0
                ? t('job_description.drawer.hierarchy.no_approvers_warning', 'This hierarchy has no approvers configured. Provisioning requests using it will fail.')
                : (selectedHier.step_count === 1
                    ? t('job_description.drawer.hierarchy.route_one', 'Provisioning requests for this job will route through {{count}} approver in sequence.', { count: selectedHier.step_count })
                    : t('job_description.drawer.hierarchy.route_other', 'Provisioning requests for this job will route through {{count}} approvers in sequence.', { count: selectedHier.step_count }))}
            </div>
          )}
          {isCreate && defaultHier && hierarchyId === defaultHier.approval_hierarchy_id && (
            <div className="mt-1 text-csm-2xs text-csm-info">
              {t('job_description.drawer.hierarchy.preselected_default', 'Pre-selected from the default hierarchy.')}
            </div>
          )}
        </div>
      </PersonalizedField>

      <PersonalizedField componentKey={CK} fieldKey="default_roles" label={t('job_description.drawer.default_roles.label', 'Default Roles')}>
      <div className="mb-3">
        <label className="mb-1 block text-csm-sm font-medium text-csm-ink-soft">
          {t('job_description.drawer.default_roles.label', 'Default Roles')}
        </label>
        <p className="mb-1 text-csm-2xs text-csm-muted">
          {t('job_description.drawer.default_roles.hint', 'Roles pre-selected when a new-user request picks this job. Requesters can adjust before submitting.')}
        </p>
        <div className="max-h-48 overflow-auto rounded border border-csm-border">
          <table className="w-full text-csm-sm">
            <tbody>
              {allRoles.map(r => (
                <tr key={r.role_id}
                    className="cursor-pointer border-t border-csm-border-soft hover:bg-csm-surface-alt"
                    onClick={() => setSelectedRoleIds(prev => {
                      const next = new Set(prev);
                      if (next.has(r.role_id)) next.delete(r.role_id); else next.add(r.role_id);
                      return next;
                    })}>
                  <td className="w-6 px-1 py-1 text-center">
                    <input type="checkbox" readOnly checked={selectedRoleIds.has(r.role_id)} className="h-3.5 w-3.5 accent-gray-700" />
                  </td>
                  <td className="px-2 py-1">{r.display_text}</td>
                  <td className="px-2 py-1 font-mono text-csm-2xs text-csm-muted">{r.internal_code}</td>
                </tr>
              ))}
              {allRoles.length === 0 && (
                <tr><td colSpan={3} className="px-2 py-3 text-center italic text-csm-watermark">
                  {t('job_description.drawer.default_roles.empty', 'No active roles.')}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </PersonalizedField>

      {/* Translations */}
      <PersonalizedField componentKey={CK} fieldKey="translations" label={t('job_description.drawer.translations.label', 'Translations')}>
        <TranslationsEditor
          value={translations}
          onChange={(rows) => setTranslations(rows.map(r => ({ locale_code: r.locale_code, display_text: r.display_text, description: r.description ?? '' })))}
          showDescription
          label={t('job_description.drawer.translations.label', 'Translations')}
        />
      </PersonalizedField>

      {error && (
        <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// §17.B13 drill-down — this page OWNS the job-description editor and registers
// it for other pages' related-children panels. Module-load side effect, so no
// export and no cross-page import (see RoleListPage for the same pattern).
// ---------------------------------------------------------------------------
function JobChildEditor({ session, pk, onClose, onSaved }: ChildEditorProps) {
  const { t } = useTranslation();
  const activeLocale = useActiveLocale();
  const [row, setRow] = useState<JobRow | null>(null);
  const [hierarchies, setHierarchies] = useState<HierarchyOpt[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // Same two reads the page performs, scoped to the one job being opened.
        const [{ data: jData, error: jErr }, { data: hData, error: hErr }] = await Promise.all([
          apiDb.from('csm_job_description')
            .select('job_description_id, internal_code, is_active, approval_hierarchy_id, csm_job_description_tl ( locale_code, display_text )')
            .eq('job_description_id', pk).maybeSingle(),
          apiDb.from('csm_approval_hierarchy')
            .select('approval_hierarchy_id, internal_code, is_default, is_active, csm_approval_hierarchy_tl ( locale_code, display_text ), csm_approval_hierarchy_step ( approval_hierarchy_step_id, is_active )')
            .eq('is_active', 'Y').order('internal_code'),
        ]);
        if (cancelled) return;
        if (jErr) throw jErr;
        if (hErr) throw hErr;

        const pick = (tls: TlRow[] | undefined) => {
          const a = tls ?? [];
          return (a.find(x => x.locale_code === activeLocale)
               ?? a.find(x => x.locale_code === 'EN_US') ?? a[0])?.display_text ?? '';
        };
        type RawHier = {
          approval_hierarchy_id: number; internal_code: string; is_default: 'Y' | 'N'; is_active: 'Y' | 'N';
          csm_approval_hierarchy_tl?: TlRow[];
          csm_approval_hierarchy_step?: { approval_hierarchy_step_id: number; is_active: 'Y' | 'N' }[];
        };
        const hOpts: HierarchyOpt[] = ((hData ?? []) as RawHier[]).map(h => ({
          approval_hierarchy_id: h.approval_hierarchy_id,
          internal_code: h.internal_code,
          display_text: pick(h.csm_approval_hierarchy_tl) || h.internal_code,
          is_default: h.is_default,
          step_count: (h.csm_approval_hierarchy_step ?? []).filter(s => s.is_active === 'Y').length,
        }));
        setHierarchies(hOpts);

        if (jData) {
          const j = jData as {
            job_description_id: number; internal_code: string; is_active: 'Y' | 'N';
            approval_hierarchy_id: number | null; csm_job_description_tl?: TlRow[];
          };
          setRow({
            job_description_id: j.job_description_id,
            internal_code: j.internal_code,
            is_active: j.is_active,
            apr_status: '',
            display_text: pick(j.csm_job_description_tl) || j.internal_code,
            tl: j.csm_job_description_tl ?? [],
            approval_hierarchy_id: j.approval_hierarchy_id,
            hierarchy_label: hOpts.find(h => h.approval_hierarchy_id === j.approval_hierarchy_id)?.display_text ?? '',
          });
        }
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pk, activeLocale]);

  if (busy || error || !row) {
    return (
      <Modal title={t('jobs.edit_title', 'Job Description')} onClose={onClose} width="lg">
        <div className={'px-1 py-3 text-csm-sm ' + (error ? 'text-csm-danger' : 'text-csm-muted')}>
          {error ?? (busy ? t('common.loading', 'Loading…') : t('common.not_found', 'Record not found.'))}
        </div>
      </Modal>
    );
  }
  return <JobEditModal session={session} row={row} hierarchies={hierarchies} onClose={onClose} onSaved={onSaved} />;
}

registerChildEditor('csm_job_description', {
  pageCode: PAGE_CODE,
  title: 'Job Description',
  Editor: JobChildEditor,
});
